import { useForm } from "react-hook-form";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useEffect, useState } from "react";
import { useLabReportStatusListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatusAPI";
import { ILabReportListState, setLabReportListFilter } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { useLabReportLabPartnerListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportLabPartnerAPI";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { PRIMARY_BUTTON } from "@/constants";

export interface ILabReportFilterProps {
  startDate: string;
  endDate: string;
  status: Array<string>;
  cities: Array<string>;
  clinics: Array<string>;
  labPartner: Array<string>;
}

interface Props {
  resetSelectedStatus: () => void;
}

const LabReportFilter = ({ resetSelectedStatus }: Props) => {
  const [_searchQuery, setSearchQuery] = useState<string>("");
  const { cities, clinics } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const { statusList, labPartnerList } = useRouteInfo("LAB_REPORT")?.routeState?.state as ILabReportListState;
  const { mappedClinicId, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const fetchStatusList = useLabReportStatusListAPI();
  const fetchLabPartnerList = useLabReportLabPartnerListAPI();
  const fetchPWLClinics = useStaffClinicListAPI("INVENTORY");
  const today = new Date();
  const twoWeeksFromNow = new Date();
  twoWeeksFromNow.setDate(today.getDate() - 30);
  const [selectionDate, setSelectionDate] = useState({
    startDate: twoWeeksFromNow,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
  const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;

  const fetchStatsList = useLabReportStatsListAPI();
  const { execute: fetchLabReports } = useLabReportFilterListAPI();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerId } = routeState.state as ICustomerProfileState;
  const [initialRender, setInitialRender] = useState(false);
  const clinicsOrgItem = useSelectMenuReducer(clinics, "display_name", "id");
  const statusOptions = useSelectMenuReducer(statusList, "status", "id");
  const labPartnerOptions = useSelectMenuReducer(labPartnerList, "lab_name", "lab_id");
  const location = useLocation();
  const isLabTestUrl = location.pathname.includes("lab-test");
  const dispatch = useDispatch();

  const formHook = useForm<ILabReportFilterProps>({
    mode: "onChange",
    defaultValues: {
      cities: [mappedCityId],
      clinics: [mappedClinicId],
      status: statusOptions?.map((id) => id.id) || [],
    },
  });

  const LabType = (tab: string) => {
    if (tab === "incoming") {
      return 2;
    } else if (tab === "external") {
      return 1;
    } else if (tab === "internal") {
      return 3;
    }
  };

  useEffect(() => {
    const data = formHook.getValues();
    if (
      formattedStartDate &&
      formattedEndDate &&
      data?.cities?.length > 0 &&
      data?.clinics?.length > 0 &&
      location.pathname &&
      !initialRender
    ) {
      const labTestType = location.pathname.split("/").pop();
      const filters = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        clinic_ids: formHook.getValues("clinics"),
        status_ids: formHook.getValues("status"),
        type: LabType(labTestType),
      };
      fetchLabReports(filters);
      fetchStatsList(filters);
      dispatch(setLabReportListFilter(filters));
      setInitialRender(true);
    }
  }, [
    formattedStartDate,
    formattedEndDate,
    formHook.watch("clinics"),
    formHook.watch("labPartner"),
    location.pathname,
    initialRender,
  ]);

  const onSearch = (query: string) => {
    const labTestType = location.pathname.split("/").pop();
    setSearchQuery(query);
    const filters = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      clinic_ids: formHook.getValues("clinics"),
      status_ids: formHook.getValues("status"),
      type: labTestType,
      keyword: query === "" ? undefined : query,
    };
    fetchLabReports(filters);
  };

  const onSubmit = (data: ILabReportFilterProps) => {
    const labTestType = location?.pathname?.split("/").pop();
    const payload: any = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      status_ids: formHook.getValues("status"),
      clinic_ids: data.clinics,
      type: LabType(labTestType),
    };
    const filters = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      clinic_ids: formHook.getValues("clinics"),
      status_ids: formHook.getValues("status"),
      type: LabType(labTestType),
    };
    if (isLabTestUrl && customerId) {
      payload.customer_id = customerId;
    }
    fetchStatsList(payload);
    fetchLabReports(payload);
    dispatch(setLabReportListFilter(filters));
    resetSelectedStatus();
  };

  useEffect(() => {
    fetchCities();

    const cities = formHook.getValues("cities");
    if (cities?.length >= 0) {
      fetchPWLClinics({
        cityIds: cities,
        withProviders: false,
      });
    }

    const labTestType = location.pathname.split("/").pop();
    const type = LabType(labTestType);

    if (type) {
      fetchStatusList(type)
      fetchLabPartnerList(type as any)
    }
  }, [formHook.watch("cities"), location.pathname]);


  return (
    <div className="bg-white">
      <div className=" py-1 rounded-xl flex flex-col md:flex-row md:flex-wrap w-full gap-3">
        <form
          className="w-full flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center gap-3 md:gap-4 "
          onSubmit={formHook.handleSubmit(onSubmit)}
        >
          <div className="w-full md:w-auto md:min-w-[220px]">
            <OMCalendarComponent
              isPaddingOnTop={false}
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              handelClick={() => {
                console.log();
              }}
            />
          </div>

          <div className="w-full md:flex-1 md:min-w-[180px] md:max-w-[260px]">
            <SearchableMultiSelectMenu
              withSelectAll
              errorMessages={[{ type: "required", message: "" }]}
              onSelectItem={(item) => {
                if (item) {
                  const statusId = item.map((i) => i.id);
                  formHook.setValue("cities", statusId);
                }
              }}
              fieldError={formHook.formState.errors.cities}
              register={formHook.register("cities", { required: false })}
              selectItems={cityOrgItem}
              placeholder="Select City"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              withDropDownSearch
              defaultSelected={cityOrgItem?.filter((c) => formHook.watch("cities")?.includes(c.id))}
              listBoxClassName="w-full"
              className="text-gray-800 h-9"
              containerClassName="w-full"
            />
          </div>
          <div className="w-full md:flex-1 md:min-w-[220px] md:max-w-[320px]">
            <SearchableMultiSelectMenu
              withSelectAll
              // defaultSelectAll
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const clinicsId = item.map((i) => i.id);
                  formHook.setValue("clinics", clinicsId);
                }
              }}
              disabled={!formHook.getValues("cities")}
              fieldError={formHook.formState.errors.clinics}
              register={formHook.register("clinics")}
              selectItems={clinicsOrgItem}
              placeholder="Select Clinics"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicsOrgItem?.filter((oc) => formHook.watch("clinics")?.includes(oc.id))}
              listBoxClassName="w-full"
              className="text-gray-800 h-9"
              containerClassName="w-full"
            />
          </div>

          <div
            className={`w-full md:w-2/12 ${location.pathname === "/app/lab-report/external" ? "visible" : "hidden"}`}
          >
            <SearchableMultiSelectMenu
              withSelectAll
              defaultSelectAll={true}
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) {
                  const labId = item.map((i) => i.id);
                  formHook.setValue("labPartner", labId);
                }
              }}
              fieldError={formHook.formState.errors.labPartner}
              register={formHook.register("labPartner")}
              selectItems={labPartnerOptions}
              placeholder="Select Lab Partner"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={labPartnerOptions?.filter((oc) => formHook.watch("labPartner")?.includes(oc.id))}
              listBoxClassName="w-full"
              className="text-gray-800 "
              containerClassName="w-full"
            />
          </div>
          <div className="w-full md:w-auto">
            <input
              type="submit"
              className={`${PRIMARY_BUTTON} w-full md:w-auto cursor-pointer`}
              value={"Apply"}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
export default LabReportFilter;
